import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { LogIn, UserPlus, Search, Pencil } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../services/api.ts'
import { useAuth } from '../context/AuthContext.tsx'
import { isSystemAdmin } from '../utils/roles.ts'
import { AGENCY_ROLES } from '../constants/roles.ts'
import AgencyAdminAccess from '../components/AgencyAdminAccess.tsx'
import { TableHeader, TableContainer } from '../components/Skeleton.tsx'
import { useColumnResize } from '../hooks/useColumnResize.ts'
import ConfirmModal from '../components/ConfirmModal.tsx'

interface AgencyUser {
  id: string
  user_id: string
  agency_id: string
  role: string
  users: {
    id: string
    name: string
    email: string
    created_at: string
  }
}

const AgencyUserManagement: React.FC = () => {
  const { user, impersonateUser } = useAuth()
  const navigate = useNavigate()
  const canImpersonate = isSystemAdmin(user)
  const [agencyUsers, setAgencyUsers] = useState<AgencyUser[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const { widths, startResize } = useColumnResize('agency-users-col-widths')

  // Invite form
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [formData, setFormData] = useState({ email: '', name: '', role: 'MEMBER' })
  const [inviting, setInviting] = useState(false)

  // Confirm remove
  const [removeTarget, setRemoveTarget] = useState<{ id: string; name: string } | null>(null)

  // Edit user
  const [editingUser, setEditingUser] = useState<AgencyUser | null>(null)
  const [editName, setEditName] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [editRole, setEditRole] = useState('MEMBER')
  const [editSaving, setEditSaving] = useState(false)

  const openEditModal = (au: AgencyUser) => {
    setEditingUser(au)
    setEditName(au.users.name || '')
    setEditEmail(au.users.email)
    setEditRole(au.role)
  }

  const handleEditSave = async () => {
    if (!editingUser) return
    setEditSaving(true)
    try {
      await api.put(`/api/admin/users/${editingUser.users.id}`, {
        name: editName.trim() || null,
        email: editEmail.trim(),
      })
      if (editRole !== editingUser.role) {
        await api.put(`/api/agency/users/${editingUser.users.id}/role`, { role: editRole })
      }
      toast.success('User updated')
      setEditingUser(null)
      fetchAgencyUsers()
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update user')
    } finally {
      setEditSaving(false)
    }
  }

  useEffect(() => {
    fetchAgencyUsers()
  }, [])

  const fetchAgencyUsers = async () => {
    setLoading(true)
    try {
      const response = await api.get('/api/agency/users')
      setAgencyUsers(response.data.agencyUsers || [])
    } catch (err) {
      toast.error('Failed to load agency users')
    } finally {
      setLoading(false)
    }
  }

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    setInviting(true)
    try {
      await api.post('/api/agency/users', formData)
      toast.success(`Invited ${formData.name || formData.email}`)
      setFormData({ email: '', name: '', role: 'MEMBER' })
      setShowInviteForm(false)
      fetchAgencyUsers()
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to invite user')
    } finally {
      setInviting(false)
    }
  }

  const handleRemoveUser = async () => {
    if (!removeTarget) return
    try {
      await api.delete(`/api/agency/users/${removeTarget.id}`)
      toast.success(`${removeTarget.name} removed from agency`)
      setRemoveTarget(null)
      fetchAgencyUsers()
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to remove user')
      setRemoveTarget(null)
    }
  }

  const handleImpersonate = async (userId: string) => {
    try {
      await impersonateUser(userId)
      navigate('/')
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to log in as user')
    }
  }

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(key)
      setSortDir('asc')
    }
  }

  const filtered = agencyUsers.filter(au => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      (au.users.name || '').toLowerCase().includes(q) || au.users.email.toLowerCase().includes(q)
    )
  })

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1
    switch (sortBy) {
      case 'email':
        return a.users.email.localeCompare(b.users.email) * dir
      case 'role':
        return a.role.localeCompare(b.role) * dir
      case 'created_at': {
        const at = a.users.created_at ? new Date(a.users.created_at).getTime() : 0
        const bt = b.users.created_at ? new Date(b.users.created_at).getTime() : 0
        return (at - bt) * dir
      }
      default:
        return (a.users.name || '').localeCompare(b.users.name || '') * dir
    }
  })

  return (
    <AgencyAdminAccess>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Agency Users</h1>
            <p className="text-sm text-gray-500 mt-1">Manage users in your agency</p>
          </div>
          <button
            onClick={() => setShowInviteForm(!showInviteForm)}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <UserPlus size={16} />
            Invite User
          </button>
        </div>

        {/* Invite Form (collapsible) */}
        {showInviteForm && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Invite New User</h2>
            <form onSubmit={handleInvite} className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Full name"
                  required
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="user@example.com"
                  required
                />
              </div>
              <div className="w-32">
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={e => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="MEMBER">Member</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={inviting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {inviting ? 'Inviting...' : 'Send Invite'}
              </button>
              <button
                type="button"
                onClick={() => setShowInviteForm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
            </form>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-4 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Users Table */}
        <TableContainer
          loading={loading}
          hasData={sorted.length > 0}
          skeletonRows={5}
          skeletonCols={5}
          emptyMessage={search ? 'No users match your search' : 'No users in your agency yet'}
        >
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table
              className={`min-w-full divide-y divide-gray-200 ${Object.keys(widths).length > 0 ? 'table-fixed' : ''}`}
            >
              <TableHeader
                columns={[
                  { label: 'Name', key: 'name' },
                  { label: 'Email', key: 'email' },
                  { label: 'Role', key: 'role' },
                  { label: 'Joined', key: 'created_at' },
                  {
                    label: 'Actions',
                    key: 'actions',
                    sortable: false,
                    resizable: false,
                    align: 'right',
                  },
                ]}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={toggleSort}
                widths={widths}
                onResize={startResize}
              />
              <tbody className="bg-white divide-y divide-gray-200">
                {sorted.map(au => (
                  <tr key={au.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {au.users.name || '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{au.users.email}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          au.role === AGENCY_ROLES.ADMIN
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {au.role === AGENCY_ROLES.ADMIN ? 'Admin' : 'Member'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(au.users.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {au.users.id !== user?.id && (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(au)}
                            className="text-gray-600 hover:text-gray-800"
                            title="Edit user"
                          >
                            <Pencil size={14} />
                          </button>
                          {canImpersonate && (
                            <button
                              onClick={() => handleImpersonate(au.users.id)}
                              className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                              title="Log in as this user"
                            >
                              <LogIn size={14} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TableContainer>

        <ConfirmModal
          open={!!removeTarget}
          title="Remove User"
          message={`Remove "${removeTarget?.name}" from this agency? They will lose access to all agency companies.`}
          confirmLabel="Remove"
          confirmVariant="danger"
          onConfirm={handleRemoveUser}
          onCancel={() => setRemoveTarget(null)}
        />

        {/* Edit User Modal */}
        {editingUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50" onClick={() => setEditingUser(null)} />
            <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit User</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="User name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={editEmail}
                    onChange={e => setEditEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="user@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={editRole}
                    onChange={e => setEditRole(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="ADMIN">Admin</option>
                    <option value="MEMBER">Member</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-between mt-6">
                <button
                  onClick={() => {
                    setEditingUser(null)
                    setRemoveTarget({
                      id: editingUser.users.id,
                      name: editingUser.users.name || editingUser.users.email,
                    })
                  }}
                  className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800"
                >
                  Remove from Agency
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => setEditingUser(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEditSave}
                    disabled={editSaving || !editEmail.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {editSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AgencyAdminAccess>
  )
}

export default AgencyUserManagement
